import type { ReactNode } from 'react';

export interface DataTableColumn<Row> {
  /** Angular `matColumnDef` name; also added to the cells as `mat-column-<id>` like Material does. */
  id: string;
  header?: ReactNode;
  cell: (row: Row, index: number) => ReactNode;
  footer?: ReactNode;
  headerClassName?: string;
  /** Extra classes on the body cells; a function decides per row (Angular `[ngClass]` on `td mat-cell`). */
  cellClassName?: string | ((row: Row, index: number) => string | undefined);
  footerClassName?: string;
  /** When set, the header is a button that asks the owner to sort by this key (server-side sorting). */
  sortKey?: string;
}

/** The column the grid is currently sorted by, so the header can show the direction. */
export interface DataTableSort {
  key: string;
  ascending: boolean;
}

interface DataTableProps<Row> {
  columns: DataTableColumn<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row, index: number) => string | number;
  /** Extra classes per row (Angular `[ngClass]` on `tr mat-row`). */
  rowClassName?: (row: Row, index: number) => string | undefined;
  /** Shown as a single spanning cell when there are no rows (Angular `*matNoDataRow`). Omit for none. */
  noDataMessage?: ReactNode;
  /** Row `title` tooltip (Angular `title="..."` on `tr mat-row`). */
  rowTitle?: (row: Row, index: number) => string | undefined;
  /** Row click (Angular `(click)` on `tr mat-row`). */
  onRowClick?: (row: Row, index: number) => void;
  hideHeader?: boolean;
  showFooter?: boolean;
  /** Angular `*matHeaderRowDef="cols; sticky: true"`: header cells stick to the top of the scrolling wrapper. */
  stickyHeader?: boolean;
  className?: string;
  sort?: DataTableSort;
  onSort?: (key: string) => void;
}

/**
 * Equivalent of `<table mat-table [dataSource]>` with `matColumnDef`s. It renders Angular Material's class names
 * (`mat-mdc-table`, `mat-mdc-header-cell`, `mdc-data-table__row`, ...) so the ported table styles apply unchanged.
 */
export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  rowClassName,
  rowTitle,
  onRowClick,
  noDataMessage,
  hideHeader = false,
  showFooter = false,
  stickyHeader = false,
  className = '',
  sort,
  onSort,
}: DataTableProps<Row>) {
  return (
    <table className={`mat-mdc-table mdc-data-table__table cdk-table ${className}`.trim()}>
      {!hideHeader && (
        <thead>
          <tr className="mat-mdc-header-row mdc-data-table__header-row cdk-header-row">
            {columns.map((column) => (
              <th
                key={column.id}
                className={`mat-mdc-header-cell mdc-data-table__header-cell cdk-header-cell cdk-column-${column.id} mat-column-${column.id} ${stickyHeader ? 'mat-mdc-table-sticky mat-mdc-table-sticky-border-elem-top ' : ''}${column.headerClassName ?? ''}`.trim()}
                style={stickyHeader ? { top: 0, zIndex: 100 } : undefined}
              >
                {column.sortKey && onSort ? (
                  <button
                    type="button"
                    className="table-sort-button"
                    aria-label={`Sort by ${typeof column.header === 'string' ? column.header : column.sortKey}`}
                    onClick={() => onSort(column.sortKey!)}
                  >
                    {column.header}
                    <span
                      className="table-sort-arrow"
                      data-dir={sort?.key === column.sortKey ? (sort.ascending ? 'asc' : 'desc') : 'none'}
                      aria-hidden="true"
                    />
                  </button>
                ) : (
                  column.header
                )}
              </th>
            ))}
          </tr>
        </thead>
      )}
      <tbody className="mdc-data-table__content">
        {rows.map((row, index) => (
          <tr
            key={rowKey(row, index)}
            className={`mat-mdc-row mdc-data-table__row cdk-row ${onRowClick ? 'cursor-pointer ' : ''}${rowClassName?.(row, index) ?? ''}`.trim()}
            title={rowTitle?.(row, index)}
            onClick={onRowClick ? () => onRowClick(row, index) : undefined}
          >
            {columns.map((column) => (
              <td
                key={column.id}
                className={`mat-mdc-cell mdc-data-table__cell cdk-cell cdk-column-${column.id} mat-column-${column.id} ${(typeof column.cellClassName === 'function' ? column.cellClassName(row, index) : column.cellClassName) ?? ''}`.trim()}
              >
                {column.cell(row, index)}
              </td>
            ))}
          </tr>
        ))}
        {rows.length === 0 && noDataMessage !== undefined && (
          <tr className="mat-mdc-row mdc-data-table__row mat-mdc-no-data-row">
            <td className="mat-mdc-cell mdc-data-table__cell" colSpan={columns.length}>
              {noDataMessage}
            </td>
          </tr>
        )}
      </tbody>
      {showFooter && (
        <tfoot>
          <tr className="mat-mdc-footer-row mdc-data-table__row cdk-footer-row">
            {columns.map((column) => (
              <td
                key={column.id}
                className={`mat-mdc-footer-cell mdc-data-table__cell cdk-footer-cell cdk-column-${column.id} mat-column-${column.id} ${column.footerClassName ?? ''}`.trim()}
              >
                {column.footer}
              </td>
            ))}
          </tr>
        </tfoot>
      )}
    </table>
  );
}
